import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isCancel } from 'axios'
import { getApiClient } from '../api'
import type {
  FiltersResponse, ActiveFilters, ChartType, SourceDetail,
} from '../types/Stats'
import { STATS_DEFAULT_GEO, STATS_DEFAULT_METRIC } from '../Constants'

const KNOWN_URL_KEYS = new Set([
  'scope', 'geo', 'metric', 'chartType',
  'source', 'unit', 'compareGeo', 'yearFrom', 'yearTo',
])

function deriveDefaultFilters(
  source: string,
  detail: SourceDetail | undefined,
): ActiveFilters {
  const dims        = detail?.dims  ?? {}
  const currentYear = new Date().getFullYear()
  const range       = detail?.range ?? { min: 2000, max: currentYear }

  return {
    source,
    unit:       dims.unit?.[0] ?? '',
    compareGeo: [],
    yearRange:  [range.min, range.max],
    ...Object.fromEntries(
      Object.entries(dims)
        .filter(([k]) => k !== 'unit')
        .map(([k, v]) => [k, v[0]])
    ),
  }
}

function filtersFromUrl(
  params: URLSearchParams,
  source: string,
  detail: SourceDetail | undefined,
): ActiveFilters {
  const base = deriveDefaultFilters(source, detail)
  const dims = detail?.dims ?? {}

  // Unit musí byť v povolenej množine pre daný source — inak fallback na default.
  const urlUnit    = params.get('unit')
  const validUnits = dims.unit ?? []
  const unit       = (urlUnit && validUnits.includes(urlUnit)) ? urlUnit : undefined

  // Dim filtre — kľúč musí byť v katalógu a hodnota v povolenej množine.
  const extraDims = Object.fromEntries(
    [...params.entries()].filter(([k, v]) => {
      if (KNOWN_URL_KEYS.has(k)) return false
      const allowedValues = dims[k]
      return Array.isArray(allowedValues) && allowedValues.includes(v)
    })
  )

  // Year range — check
  const yearFromN   = Number(params.get('yearFrom'))
  const yearToN     = Number(params.get('yearTo'))
  const currentYear = new Date().getFullYear()
  const yearRange: [number, number] | undefined = (
    Number.isFinite(yearFromN) && Number.isFinite(yearToN) &&
    yearFromN >= 1900 && yearToN <= currentYear &&
    yearFromN <= yearToN
  ) ? [yearFromN, yearToN] : undefined

  const compareGeo = params.getAll('compareGeo')

  return {
    ...base,
    source,
    ...(unit                  ? { unit }       : {}),
    ...(compareGeo.length > 0 ? { compareGeo } : {}),
    ...(yearRange             ? { yearRange }  : {}),
    ...extraDims,
  }
}

function filtersToUrlParams(
  filters: ActiveFilters,
): Record<string, string | string[]> {
  const { source, unit, compareGeo, yearRange, ...dims } = filters
  return {
    source,
    unit,
    ...(compareGeo.length > 0 ? { compareGeo } : {}),
    yearFrom: String(yearRange[0]),
    yearTo:   String(yearRange[1]),
    ...Object.fromEntries(
      Object.entries(dims).map(([k, v]) => [k, String(v)])
    ),
  }
}

const INITIAL_FILTERS: ActiveFilters = {
  source:     '',
  unit:       '',
  compareGeo: [],
  yearRange:  [2000, new Date().getFullYear()],
}

export function useStatsFilters(regions: string[]) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [scope,  setScope]  = useState<string>(() =>
    searchParams.get('scope') || regions[0] || ''
  )
  const [geo,    setGeo]    = useState<string>(() =>
    searchParams.get('geo') || STATS_DEFAULT_GEO
  )
  const [metric, setMetric] = useState<string | null>(() =>
    searchParams.get('metric') || STATS_DEFAULT_METRIC
  )
  const [filtersResp,    setFiltersResp]    = useState<FiltersResponse | null>(null)
  const [chartType,      setChartType]      = useState<ChartType>(() =>
    (searchParams.get('chartType') as ChartType) || 'bar'
  )
  const [loadingFilters, setLoadingFilters] = useState(false)
  const [filtersError,   setFiltersError]   = useState(false)
  const [pendingFilters,      setPendingFilters]      = useState<ActiveFilters>(INITIAL_FILTERS)
  const [appliedFilters,      setAppliedFilters]      = useState<ActiveFilters>(INITIAL_FILTERS)
  const [availableCompareGeo, setAvailableCompareGeo] = useState<string[]>([])

  const filtersAbortRef  = useRef<AbortController | null>(null)
  const isFirstLoad      = useRef(true)
  const initSearchParams = useRef(searchParams)

  useEffect(() => {
    if (!scope || !geo || !metric) return

    filtersAbortRef.current?.abort()
    const controller = new AbortController()
    filtersAbortRef.current = controller

    setLoadingFilters(true)
    setFiltersError(false)
    getApiClient()
      .getFiltersByScope(scope, { geo, metric }, { signal: controller.signal })
      .then(resp => {
        const data = resp as unknown as FiltersResponse
        setFiltersResp(data)

        const firstLoad     = isFirstLoad.current
        isFirstLoad.current = false

        const initParams = initSearchParams.current
        const urlSource  = firstLoad ? initParams.get('source') : null
        const src        = (urlSource && data.details[urlSource]) ? urlSource : data.default
        const detail     = data.details[src]
        const types      = detail?.chart_types ?? ['bar']

        if (firstLoad) {
          const urlChartType = initParams.get('chartType') as ChartType
          setChartType(urlChartType && types.includes(urlChartType) ? urlChartType : types[0] as ChartType)
        } else {
          setChartType(types[0] as ChartType)
        }

        const filters = (firstLoad && initParams.get('source'))
          ? filtersFromUrl(initParams, src, detail)
          : deriveDefaultFilters(src, detail)

        setPendingFilters(filters)
        setAppliedFilters(filters)
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return
        console.error('Failed to load filters', err)
        setFiltersResp(null)
        setFiltersError(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingFilters(false)
      })

    return () => controller.abort()
  }, [scope, geo, metric])

  // Synchronizácia stavu do URL
  useEffect(() => {
    if (!appliedFilters.source || !metric) return

    setSearchParams(
      {
        scope,
        geo,
        metric,
        chartType,
        ...filtersToUrlParams(appliedFilters),
      },
      { replace: true },
    )
    // setSearchParams je stabilný z react-router
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, geo, metric, chartType, appliedFilters])

  const handleScopeChange = useCallback((s: string) => {
    setScope(s); setGeo(''); setMetric(null); setFiltersResp(null); setFiltersError(false)
    setAvailableCompareGeo([])
  }, [])

  const handleGeoChange = useCallback((g: string) => {
    setGeo(g); setMetric(null); setFiltersResp(null); setFiltersError(false)
    setAvailableCompareGeo([])
  }, [])

  const handleMetricChange = useCallback((id: string | null) => {
    setMetric(id); setFiltersResp(null); setFiltersError(false)
    setAvailableCompareGeo([])
  }, [])

  const handleSourceChange = useCallback((src: string) => {
    if (!filtersResp) return
    const detail  = filtersResp.details[src]
    const filters = deriveDefaultFilters(src, detail)
    setPendingFilters(filters)
    setAppliedFilters(filters)
  }, [filtersResp])

  const pendingFiltersRef = useRef(pendingFilters)
  pendingFiltersRef.current = pendingFilters

  const handleApply = useCallback(
    () => setAppliedFilters(pendingFiltersRef.current),
    [],
  )

  const handlePendingChange = useCallback((f: ActiveFilters) => {
    const current     = pendingFiltersRef.current
    const frontendOnly = new Set(['yearRange', 'compareGeo'])
    const backendChanged = Object.keys({ ...current, ...f }).some(
      k => !frontendOnly.has(k) && f[k] !== current[k]
    )
    setPendingFilters(backendChanged ? { ...f, compareGeo: [] } : f)
  }, [])

  const handleCompareGeoChange = useCallback((list: string[]) => {
    setPendingFilters(prev => ({ ...prev, compareGeo: list }))
  }, [])

  return {
    scope, geo, metric,
    filtersResp, chartType, loadingFilters, filtersError,
    pendingFilters, appliedFilters,
    availableCompareGeo, setAvailableCompareGeo,
    setChartType,
    handleScopeChange, handleGeoChange, handleMetricChange,
    handleSourceChange, handlePendingChange, handleApply,
    handleCompareGeoChange,
  }
}
